import { cloneDeep, uniqueId } from 'lodash';
import React, { useMemo, useState ,useEffect} from 'react';
import { Modal } from 'react-bootstrap';
import AssetAddEditForm from '../../../../../forms/AssetAddEditForm/AssetAddEditForm';
import { APARTMENT_TYPES, ASSET_CATEGORIES, primaryBankAccountName } from '../../../../../helpers/constants';
import useAuth from '../../../../../hooks/useAuth';
import CollapsibleSection from '../../../../common/CollapsibleSection';
import ExplainatoryVideoButton from '../../../../common/ExplainatoryVideoButton';
import useLocalization from '../../../../../hooks/useLocalization';
import OneOffEventSheet from './OneOffEventSheet';

const AssetModal = ({
  show,
  toBeEditedAsset,
  onHide,
  strategy,
  showProgress,
  strategyYears = [],
  compTable,
  onAssetSubmit,
  incompleteYearsOfStrategy,
  activeCustomer,
  adminCompTable,
  onEditOneOffEvent,
  onDeleteOneOffEvent,
  onAddOneOffEvent,
  readOnlyMode = false
}) => {
  const { user } = useAuth();
  const { translate } = useLocalization();
  const [oneOffCollapsed, setOneOffCollapsed] = useState(true);
  
  useEffect(() => {
    if (show) {
      setOneOffCollapsed(true);
    }
  }, [show]);

  const assetOptions = useMemo(() => strategy.assets, [strategy]);

  const initialFormValues = useMemo(
    () =>
      !toBeEditedAsset
        ? {
            category: ASSET_CATEGORIES.CASH,
            editMode: strategy.isDefaultSetupMode ? 'Add Mode' : 'Buy Mode',
            buyingYear: strategy.initialYear,
            buyingMonth: strategy.initialMonth,
            sellingMonth: strategy.initialMonth,
            onlyApartment: { buying: true, selling: true },
            linkedBankAccount: assetOptions.find(a => a.name === primaryBankAccountName)?._id,
            liquidationScore: 1,
            partialSales: [],
            rsuGrants: [],
            paperApartmentPayments: [],
            deposits: []
          }
        : cloneDeep({
            ...toBeEditedAsset,
            editMode: toBeEditedAsset.editMode ? 'Edit Mode' : 'Setup Mode',
            //fixed incomes have startYear instead of buyingYear
            buyingYear: toBeEditedAsset.buyingYear ?? toBeEditedAsset.startYear,
            buyingMonth: toBeEditedAsset.buyingMonth ?? toBeEditedAsset.startMonth,
            sellingYear: toBeEditedAsset.sellingYear ?? toBeEditedAsset.endYear,
            sellingMonth: toBeEditedAsset.sellingMonth ?? toBeEditedAsset.endMonth,
            partialSales: toBeEditedAsset?.partialSales.map(s => ({ ...s, id: uniqueId() })),
            rsuGrants: toBeEditedAsset?.rsuGrants.map(s => ({ ...s, id: uniqueId() })),
            paperApartmentPayments: toBeEditedAsset?.paperApartmentPayments.map(s => ({ ...s, id: uniqueId() }))
          }),
    [toBeEditedAsset, strategy, show]
  );

  return (
    <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={!showProgress}>
        <Modal.Title className="flex-1">
          <h6 className="mb-0">{translate(toBeEditedAsset ? 'update_asset' : 'add_new_asset')}</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto px-4">
        {show && (
          <>
            <div className="px-2">
              <ExplainatoryVideoButton videoKey="add_asset" />
            </div>

            <AssetAddEditForm
              initialFormValues={initialFormValues}
              onSubmit={onAssetSubmit}
              strategy={strategy}
              strategyYears={strategyYears}
              incompleteYearsOfStrategy={incompleteYearsOfStrategy}
              assetOptions={assetOptions}
              compTable={compTable}
              adminCompTable={adminCompTable}
              user={user}
              activeCustomer={activeCustomer}
              showProgress={showProgress}
              afterFormContent={
                toBeEditedAsset && onEditOneOffEvent && onDeleteOneOffEvent ? (
                  <CollapsibleSection
                    key={toBeEditedAsset._id}
                    sectionName="one_time_events"
                    isCollapsed={oneOffCollapsed}
                    onShowHideClick={() => setOneOffCollapsed(prev => !prev)}
                  >
                    <div className="px-2 pb-2">
                      <OneOffEventSheet
                        strategy={strategy}
                        onEditEventClick={onEditOneOffEvent}
                        onOneOffEventDeleteClick={onDeleteOneOffEvent}
                        onAddEventClick={
                          onAddOneOffEvent ? () => onAddOneOffEvent(toBeEditedAsset) : undefined
                        }
                        editable={!readOnlyMode}
                        filterByInstrument={{ refId: toBeEditedAsset._id, type: 'Asset' }}
                      />
                    </div>
                  </CollapsibleSection>
                ) : null
              }
            />
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AssetModal;
